/**
 * Filtering and sorting.
 *
 * A progressive enhancement over markup that already works: the facets are a real GET
 * form and the sort control is a real <select>, so with JavaScript off the page submits
 * and reloads filtered. This file removes the reload.
 *
 * It fetches the same URL the form would have navigated to, asks for one section back
 * via the Section Rendering API, and swaps the section's markup. Rendering stays in
 * Liquid — the alternative, building product cards in JavaScript, would mean a second
 * implementation of the card that has to be kept in step with the first and would format
 * money outside `snippets/price.liquid`.
 */

(function () {
  'use strict';

  window.Hubcart = window.Hubcart || {};
  if (window.Hubcart.facets) return;

  /**
   * Every scope that holds a copy of one logical section's facets: the section wrapper
   * itself (the toolbar, and the sidebar copy when that layout is active) plus each
   * `<drawer-element data-facets-section>` naming this section's id.
   *
   * The drawers render as siblings of the section wrapper, not descendants of it — see
   * `sections/collection.liquid`'s comment on `FacetDrawer` for why — so neither native
   * event bubbling nor a plain `section.querySelectorAll(...)` reaches into them. This is
   * the one place that reconnects the two, and everything that needs "every copy of the
   * facets for this section" — reading form state, binding listeners, painting price
   * tracks — goes through it rather than repeating its own guess at where the drawers are.
   */
  function scopesFor(section) {
    const id = section.dataset.sectionId;
    return [section, ...document.querySelectorAll(`[data-facets-section="${id}"]`)];
  }

  /**
   * Serialise every facet form on the page plus the sort control into one query string.
   *
   * Every copy — sidebar, toolbar and every drawer — is read, because a shopper can set
   * filters in one and continue in another (drawer, then resize past 1280px into the
   * sidebar; toolbar drawer, then the sticky pill's own drawer). Reading only the one that
   * fired the event would silently drop the rest's state.
   */
  function buildQuery(section) {
    const params = new URLSearchParams();

    /* The search page filters a query, not a collection: `q` and `type` are carried by
       the URL rather than by any control, and dropping them would turn "filter these
       results" into "filter the whole catalogue". */
    const current = new URLSearchParams(window.location.search);
    ['q', 'type', 'options[prefix]'].forEach((key) => {
      const value = current.get(key);
      if (value) params.set(key, value);
    });

    scopesFor(section).forEach((scope) => {
      scope.querySelectorAll('[data-facets-form]').forEach((form) => {
        new FormData(form).forEach((value, key) => {
          if (value === '' || key === 'sort_by') return;
          // Filter params repeat rather than overwrite: two colours means two values.
          if (!params.getAll(key).includes(value)) params.append(key, value);
        });
      });
    });

    const sort = section.querySelector('[data-sort-form] select');
    if (sort && sort.value) params.set('sort_by', sort.value);

    return params;
  }

  /**
   * Price range: two overlaid <input type="range"> sliders and two <input type="number">
   * fields represent the same pair of values, but only the number inputs carry a `name`
   * — the sliders exist so a value can be explored by dragging, not to be submitted
   * themselves. Moving either kind of control has to update the other, and the track's
   * gradient (`--range-min` / `--range-max` on `.facet__range`, painted in
   * `component-facets.css`) has to move with them, or the slider looks live but does
   * nothing and the painted span never matches the filter actually applied.
   */
  function paintRange(wrapper) {
    const max = Number(wrapper.dataset.rangeMax);
    const lower = wrapper.querySelector('[data-range-lower]');
    const upper = wrapper.querySelector('[data-range-upper]');
    if (!lower || !upper || !max) return;
    wrapper.style.setProperty('--range-min', `${(Number(lower.value) / max) * 100}%`);
    wrapper.style.setProperty('--range-max', `${(Number(upper.value) / max) * 100}%`);
  }

  /* A dragged handle stops at its partner rather than crossing it, so the lower value
     can never read higher than the upper one on screen. */
  function syncRangeToNumber(range) {
    const wrapper = range.closest('[data-price-range]');
    const group = wrapper.closest('.facet__price');
    const lower = wrapper.querySelector('[data-range-lower]');
    const upper = wrapper.querySelector('[data-range-upper]');
    const lowerNumber = group.querySelector('[data-price-input="lower"]');
    const upperNumber = group.querySelector('[data-price-input="upper"]');

    if (range === lower && Number(lower.value) > Number(upper.value)) lower.value = upper.value;
    if (range === upper && Number(upper.value) < Number(lower.value)) upper.value = lower.value;

    lowerNumber.value = lower.value;
    upperNumber.value = upper.value;
    paintRange(wrapper);
  }

  /* Moves the handles to match what has been typed, clamped into the filter's real range
     and uncrossed. The typed text itself is left exactly as entered — clamping mid
     keystroke would fight the caret, rewriting the "9" on the way to "90" into the
     maximum. `commitNumbers` below is what reconciles the two, once typing has stopped. */
  function syncNumberToRange(number) {
    const group = number.closest('.facet__price');
    const wrapper = group.querySelector('[data-price-range]');
    const max = Number(wrapper.dataset.rangeMax);
    const lower = wrapper.querySelector('[data-range-lower]');
    const upper = wrapper.querySelector('[data-range-upper]');
    const lowerNumber = group.querySelector('[data-price-input="lower"]');
    const upperNumber = group.querySelector('[data-price-input="upper"]');

    let lowerValue = lowerNumber.value === '' ? 0 : Number(lowerNumber.value);
    let upperValue = upperNumber.value === '' ? max : Number(upperNumber.value);
    lowerValue = Math.min(Math.max(lowerValue, 0), max);
    upperValue = Math.min(Math.max(upperValue, 0), max);
    if (lowerValue > upperValue) {
      if (number === lowerNumber) upperValue = lowerValue;
      else lowerValue = upperValue;
    }

    lower.value = lowerValue;
    upper.value = upperValue;
    paintRange(wrapper);
  }

  /**
   * Writes the handles' settled values back into the number fields, on blur rather than
   * on every keystroke.
   *
   * Only the number inputs carry a `name`, so they alone are what the form submits. Until
   * this runs the two can disagree: a typed 99999 leaves the handle at the maximum while
   * the field still submits 99999, and a lower bound typed above the upper one submits a
   * crossed pair that can only ever return nothing. Reconciling here means the number a
   * shopper is looking at is the number actually filtered on.
   */
  function commitNumbers(number) {
    const group = number.closest('.facet__price');
    const wrapper = group.querySelector('[data-price-range]');
    if (!wrapper) return;
    syncNumberToRange(number);
    group.querySelector('[data-price-input="lower"]').value = wrapper.querySelector('[data-range-lower]').value;
    group.querySelector('[data-price-input="upper"]').value = wrapper.querySelector('[data-range-upper]').value;
  }

  /**
   * Make every copy of the facets agree with the filter state that was just applied.
   *
   * Neither path that changes the results updates the drawers: `apply()` deliberately
   * leaves the controls in place, and `navigate()` replaces the section wrapper — but the
   * drawers are siblings of that wrapper rather than descendants of it (see `scopesFor`),
   * so the swap goes straight past them. Without this, removing a filter by its chip
   * leaves that filter's checkbox still ticked inside the drawer, and since `buildQuery()`
   * reads every scope, the shopper's next click would silently put back the filter they
   * had just taken off.
   *
   * The state is read from the query string rather than copied between the DOM copies,
   * because the query string is what the server just filtered on — the one description of
   * the current state that cannot itself have gone stale.
   */
  function syncControls(section, params) {
    scopesFor(section).forEach((scope) => {
      scope.querySelectorAll('[data-facets-form]').forEach((form) => {
        form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = params.getAll(input.name).includes(input.value);
        });

        /* An absent price parameter means that bound is not set, which is the empty
           field — not zero. `syncNumberToRange` below then puts the handles at the ends
           of the track and repaints it. */
        form.querySelectorAll('[data-price-input]').forEach((input) => {
          input.value = params.get(input.name) || '';
        });
      });

      scope.querySelectorAll('[data-price-input="lower"]').forEach(syncNumberToRange);

      /* Sort is left alone when the URL does not carry it: a chip's removal link keeps
         whatever sort was in effect, so an absent `sort_by` means "unchanged", and
         resetting the select to its first option would misreport the order on screen. */
      const sort = params.get('sort_by');
      if (sort) {
        scope
          .querySelectorAll('[data-sort-select], [data-sort-form] select')
          .forEach((select) => {
            select.value = sort;
          });
      }
    });
  }

  async function apply(section) {
    const sectionId = section.dataset.sectionId;
    const params = buildQuery(section);
    const url = `${window.location.pathname}?${params.toString()}`;

    const results = section.querySelector('[data-results]');
    if (results) results.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${url}${params.toString() ? '&' : ''}section_id=${sectionId}`);
      if (!response.ok) throw new Error(response.statusText);
      const markup = await response.text();

      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = doc.querySelector(`[data-section-id="${sectionId}"]`);
      if (!fresh) throw new Error('section markup missing');

      /* The reference marks the grid of every filtered render `motion-reduced` before
         it lands, so results change in place rather than replaying the entry cascade.
         `data-static` is our name for the same silence (`component-product-card.js`). */
      fresh
        .querySelectorAll('motion-grid')
        .forEach((grid) => grid.setAttribute('data-static', ''));

      /* Swap the results, the count and the chips — but not the facet controls
         themselves. Replacing the inputs mid-interaction would close every open
         <details>, drop focus, and on a phone dismiss the keyboard between two price
         digits. `syncControls` below then reconciles which values are ticked without
         touching the elements themselves; only the per-value counts stay stale until the
         next full load, which is the cheaper of the two costs. */
      const swap = ['[data-results]', '[data-results-count]', '[data-active-facets]'];
      swap.forEach((selector) => {
        const next = fresh.querySelector(selector);
        const current = section.querySelector(selector);
        if (next && current) {
          current.replaceWith(next);
        } else if (!next && current) {
          current.remove();
        }
      });

      /* The drawers' footers are the one piece of chrome the swap above cannot reach:
         they sit outside `[data-section-id]` (see `scopesFor`), so a filtered render left
         "View results 15" on the button that closes the drawer while the grid behind it
         held 13, and the "Clear all" link — which only renders when a filter is active —
         never appeared until the next full page load. The footer carries no input state,
         only the count and that link, so replacing it outright costs nothing; every copy
         is identical, so the first one in the fresh document serves them all. */
      const freshFooter = doc.querySelector('.facets__footer');
      if (freshFooter) {
        scopesFor(section).forEach((scope) => {
          scope
            .querySelectorAll('.facets__footer')
            .forEach((footer) => footer.replaceWith(freshFooter.cloneNode(true)));
        });
      }

      /* The openers' badges have the same reach problem in the other direction: both
         `<facet-count>` elements sit inside the wrapper but outside all three swapped
         regions, so the toolbar button and the phone's sticky pill went on reporting no
         active filters while one was applied. Their text is copied rather than the
         element swapped, because one of them is inside the button the shopper is pressing.
      */
      const freshBadge = doc.querySelector('facet-count');
      if (freshBadge) {
        scopesFor(section).forEach((scope) => {
          scope.querySelectorAll('facet-count').forEach((badge) => {
            badge.textContent = freshBadge.textContent;
            badge.toggleAttribute('hidden', freshBadge.hasAttribute('hidden'));
          });
        });
      }

      syncControls(section, params);

      /* `pushState`, so Back returns to the previous filter state rather than leaving
         the collection entirely, and so the filtered view can be shared as a link. */
      window.history.pushState({ url }, '', url);

      document.dispatchEvent(
        new CustomEvent('hubcart:sections:load', { detail: { root: section } })
      );
    } catch (error) {
      /* Fall back to the navigation the form would have performed. A filter that
         silently does nothing is worse than one that costs a page load. */
      window.location.href = url;
    } finally {
      const current = section.querySelector('[data-results]');
      if (current) current.removeAttribute('aria-busy');
    }
  }

  /**
   * Binds one logical section's listeners onto one of its scopes — the wrapper itself, or
   * one of its drawers (see `scopesFor`). `scope` is where the listener lives and what it
   * hears; `section` is always the logical section, passed straight through to
   * `apply()`/`navigate()`/the sort mirror, so a filter picked in a drawer applies to the
   * one section on the page regardless of which scope the input lived in.
   */
  function bindScope(scope, section) {
    /* Paints every price track from its current input values on every call, not just
       the first: `apply()` deliberately leaves the facet controls in place, so their own
       values never go stale, but `navigate()` replaces the whole section with fresh
       server-rendered markup whose default `--range-min`/`--range-max` (0%/100%) has not
       yet been corrected for whatever range is actually active. */
    scope.querySelectorAll('[data-price-range]').forEach(paintRange);

    if (scope.dataset.facetsBound === 'true') return;
    scope.dataset.facetsBound = 'true';

    /* Continuous drag or keystroke feedback — kept separate from the 'change' listener
       below, which is what actually applies a filter. A slider commits on release
       ('change'), which is also when this scope's delegated 'change' listener fires
       apply(); a number field is typed and only ever applies from the footer submit,
       matching every other price input in this form. */
    scope.addEventListener('input', (event) => {
      if (event.target.matches('[data-range-lower], [data-range-upper]')) {
        syncRangeToNumber(event.target);
      } else if (event.target.matches('[data-price-input]')) {
        syncNumberToRange(event.target);
      }
    });

    scope.addEventListener('change', (event) => {
      if (!event.target.closest('[data-facets-form], [data-sort-form]')) return;

      /* The toolbar's sort control and every drawer's are separate inputs for one value
         — the toolbar's is hidden below 1280px, a drawer's is the only one a phone ever
         sees. `buildQuery` reads sort_by from the toolbar copy alone, so whichever one
         the shopper touches is mirrored onto every other scope, or the value just chosen
         would be lost the moment the toolbar's stale one is read instead. */
      if (event.target.matches('[data-sort-select], [data-sort-form] select')) {
        scopesFor(section).forEach((other) => {
          other.querySelectorAll('[data-sort-select], [data-sort-form] select').forEach((select) => {
            if (select !== event.target) select.value = event.target.value;
          });
        });
      }

      /* The same reconciliation for tick boxes, and it is not optional: this page renders
         the facets three times over — toolbar drawer, sticky-pill drawer, and the sidebar
         where that layout is active — and `buildQuery` unions all of them. Un-ticking a
         box in one copy therefore removes nothing, because the identical box in the copy
         next door is still ticked and puts the filter straight back; the request goes out
         unchanged and `syncControls` then re-ticks the box the shopper just cleared. The
         switch is where this shows first (it is the only filter on this catalogue that is
         a single bit), but it applies to every checkbox facet. Matched on name and value
         rather than on id, which is unique per copy by construction.
      */
      if (event.target.matches('input[type="checkbox"], input[type="radio"]')) {
        scopesFor(section).forEach((other) => {
          other
            .querySelectorAll(
              `[data-facets-form] input[name="${CSS.escape(event.target.name)}"][value="${CSS.escape(event.target.value)}"]`
            )
            .forEach((input) => {
              if (input !== event.target) input.checked = event.target.checked;
            });
        });
      }

      /* Price is typed, not picked. Applying on every keystroke would fire a request
         per digit, so those wait for the Apply button or a blur — but the pair is
         reconciled here, on blur, so the field never submits a value the handle
         already disagrees with. */
      if (event.target.type === 'number') {
        if (event.target.matches('[data-price-input]')) commitNumbers(event.target);
        return;
      }
      apply(section);
    });

    scope.addEventListener('submit', (event) => {
      if (!event.target.closest('[data-facets-form], [data-sort-form]')) return;
      event.preventDefault();
      apply(section);
      // Whichever drawer this submit came from — the footer's "Apply" is the only submit
      // a drawer scope ever sees, so the nearest ancestor is always the right one to close.
      event.target.closest('drawer-element')?.close();
    });

    /* Chips and clear links are links. Intercepting them keeps a filter removal as
       cheap as adding one; without JavaScript they still navigate. The drawer footer's
       "Clear all" (`data-facets-clear`) lives in the drawer scopes, the chips in the
       toolbar scope — binding every scope covers both. */
    scope.addEventListener('click', (event) => {
      const chip = event.target.closest('.chip, [data-facets-clear]');
      if (!chip) return;
      event.preventDefault();
      navigate(section, chip.href);
    });
  }

  function init(root) {
    root.querySelectorAll('[data-section-id]').forEach((section) => {
      scopesFor(section).forEach((scope) => bindScope(scope, section));
    });
  }

  async function navigate(section, href) {
    const sectionId = section.dataset.sectionId;
    const url = new URL(href, window.location.origin);
    const results = section.querySelector('[data-results]');
    if (results) results.setAttribute('aria-busy', 'true');

    try {
      const separator = url.search ? '&' : '?';
      const response = await fetch(`${url.pathname}${url.search}${separator}section_id=${sectionId}`);
      if (!response.ok) throw new Error(response.statusText);
      const fresh = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector(`[data-section-id="${sectionId}"]`);
      if (!fresh) throw new Error('section markup missing');

      /* The reference marks the grid of every filtered render `motion-reduced` before
         it lands, so results change in place rather than replaying the entry cascade.
         `data-static` is our name for the same silence (`component-product-card.js`). */
      fresh
        .querySelectorAll('motion-grid')
        .forEach((grid) => grid.setAttribute('data-static', ''));

      section.replaceWith(fresh);
      /* `fresh`, not `section`: the old wrapper is detached by the line above, so
         `scopesFor` has to start from the one now in the document. The drawers it finds
         are the same elements either way — they were never replaced. */
      syncControls(fresh, url.searchParams);
      window.history.pushState({ url: url.href }, '', url.href);
      document.dispatchEvent(
        new CustomEvent('hubcart:sections:load', { detail: { root: fresh } })
      );
      init(document);
    } catch (error) {
      window.location.href = href;
    }
  }

  /* Back and Forward have to reload rather than replay, because the facet controls are
     deliberately not re-rendered on apply and so cannot be restored from history state
     alone. */
  window.addEventListener('popstate', () => window.location.reload());

  /* Run now and on every section re-render. Both are needed: this is a deferred body
     script, so `theme.js` has already dispatched the first `hubcart:sections:load`. */
  init(document);
  document.addEventListener('hubcart:sections:load', (event) => init(event.detail.root || document));

  window.Hubcart.facets = { apply };
})();

/**
 * Results-page chrome: the toolbar's pinned state and the sticky pill's reveal.
 *
 * Separate from the filtering IIFE above on purpose — nothing here touches the network
 * or the facet forms; it only mirrors two behaviours the reference keeps beside its facet
 * classes in `collection.js`:
 *
 * 1. `facets-topbar`: while the sticky toolbar's rect sits exactly at the top of the
 *    viewport, the body carries a state class (`facets-topbar--active` there,
 *    `results-toolbar--pinned` here) that sends the header away and paints the bar — the
 *    CSS half lives in `component-results.css`. The check is positional equality, not a
 *    scroll threshold: sticky positioning already encodes "arrived at the top", and when
 *    the section's end pushes the bar off, its top goes negative and the state drops on
 *    its own. The reference compares `top === 0`; ours allows sub-pixel noise.
 *
 * 2. `facet-sticky`: an IntersectionObserver over the results section with the
 *    reference's own rootMargin — `-(availHeight − 200)px` above, `+availHeight` below —
 *    so the pill is present while the shopper is anywhere near the grid and sinks away
 *    (`is-out-of-view`) once they are a full viewport past it. The reference gates its
 *    pill to below 1280px in CSS (`xl:hidden`), not here, and so do we
 *    (`component-results.css`).
 */
(function () {
  'use strict';

  const toolbar = document.querySelector('.results-toolbar');
  if (toolbar) {
    const desktop = window.matchMedia('(min-width: 1280px)');
    const PINNED = 'results-toolbar--pinned';
    let ticking = false;

    function update() {
      ticking = false;
      const pinned = desktop.matches && Math.abs(toolbar.getBoundingClientRect().top) < 1;
      document.body.classList.toggle(PINNED, pinned);
    }

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    desktop.addEventListener('change', update);
    update();
  }

  const pill = document.querySelector('.facet-sticky');
  const pillButton = pill && pill.querySelector('.facet-sticky__button');
  const resultsSection = pill && pill.closest('.shopify-section');
  if (pillButton && resultsSection) {
    const observer = new IntersectionObserver(
      (entries) => {
        pillButton.classList.toggle('is-out-of-view', !entries[0].isIntersecting);
      },
      { rootMargin: `-${screen.availHeight - 200}px 0px ${screen.availHeight}px 0px` }
    );
    observer.observe(resultsSection);
  }
})();
